/**
 * Multi-select component for managing user affiliate subscriptions
 */
import { useState, useEffect } from 'react';
import { Check, Save, Loader2 } from 'lucide-react';
import {
  useAvailableAffiliates,
  useAffiliateSubscriptions,
  useSetAffiliateSubscriptions,
} from '../hooks/useAdminUsers';
import { haptic } from '@/shared/utils/telegram';

interface AffiliateSubscriptionSelectorProps {
  telegramUserId: string;
}

export function AffiliateSubscriptionSelector({
  telegramUserId,
}: AffiliateSubscriptionSelectorProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  const availableQuery = useAvailableAffiliates();
  const subscriptionsQuery = useAffiliateSubscriptions(telegramUserId);
  const setSubscriptionsMutation = useSetAffiliateSubscriptions();

  const availableAffiliates = availableQuery.data ?? [];

  // Initialize selected affiliates from current subscriptions
  useEffect(() => {
    if (subscriptionsQuery.data && !initialized) {
      setSelectedIds(new Set(subscriptionsQuery.data.affiliateIds));
      setInitialized(true);
    }
  }, [subscriptionsQuery.data, initialized]);

  const currentSubscriptions = subscriptionsQuery.data?.affiliateIds ?? [];
  const hasChanges =
    initialized &&
    (selectedIds.size !== currentSubscriptions.length ||
      [...selectedIds].some((id) => !currentSubscriptions.includes(id)));

  const toggleAffiliate = (affiliateId: string) => {
    haptic.impact('light');
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(affiliateId)) {
        next.delete(affiliateId);
      } else {
        next.add(affiliateId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    try {
      haptic.impact('medium');
      await setSubscriptionsMutation.mutateAsync({
        telegramUserId,
        affiliateIds: [...selectedIds],
      });
      haptic.notification('success');
    } catch {
      haptic.notification('error');
    }
  };

  const isLoading = availableQuery.isLoading || subscriptionsQuery.isLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 size={20} className="animate-spin text-[var(--tg-theme-hint-color)]" />
      </div>
    );
  }

  if (availableAffiliates.length === 0) {
    return (
      <p className="text-xs text-[var(--tg-theme-hint-color)] py-2">
        No affiliate IDs found in events
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5 max-h-48 overflow-y-auto">
        {availableAffiliates.map((aff) => (
          <button
            key={aff.affiliateId}
            type="button"
            onClick={() => toggleAffiliate(aff.affiliateId)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
              selectedIds.has(aff.affiliateId)
                ? 'bg-[var(--tg-theme-link-color)]/10 border border-[var(--tg-theme-link-color)]/30'
                : 'bg-[var(--tg-theme-secondary-bg-color)] border border-transparent'
            }`}
          >
            <div
              className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                selectedIds.has(aff.affiliateId)
                  ? 'bg-[var(--tg-theme-link-color)] text-white'
                  : 'bg-[var(--tg-theme-bg-color)] border border-black/10 dark:border-white/10'
              }`}
            >
              {selectedIds.has(aff.affiliateId) && <Check size={14} />}
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-sm font-medium text-[var(--tg-theme-text-color)] block truncate">
                {aff.affiliateId}
              </span>
              <span className="text-[10px] text-[var(--tg-theme-hint-color)] block truncate">
                {aff.brands.join(', ')}
              </span>
            </div>
          </button>
        ))}
      </div>

      {hasChanges && (
        <button
          onClick={handleSave}
          disabled={setSubscriptionsMutation.isPending}
          className="w-full py-2.5 px-4 bg-[var(--tg-theme-link-color)] text-white rounded-xl text-xs font-bold uppercase tracking-wide hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Save size={14} />
          {setSubscriptionsMutation.isPending
            ? 'Saving...'
            : 'Save Subscriptions'}
        </button>
      )}
    </div>
  );
}
